import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    startAfter,
    Timestamp,
    deleteField,
    onSnapshot,
    serverTimestamp,
    type Unsubscribe,
} from "firebase/firestore";
import { format, isSameDay, isBefore, startOfDay, addDays, differenceInDays } from "date-fns";
import { parseInParis, formatInParis } from "./timezone";
import { db } from "./firebase";
import { parseBookingPayload } from "@/lib/booking-payload";
import { deleteQualiReparAsset, type StoredQualiReparAsset } from "@/lib/qualirepar-assets";
import type { EventType, Booking, AvailabilityRule, TimeSlot, Client, Admin, AdminRole, VatScheme, AppSettings, EventChangeResponse } from "@/types";
import type { EmailTemplateContent, EmailTemplateKey, EmailTemplateOverrides } from "../../shared/email-templates";

// ─── Helpers ────────────────────────────────────────────────────────────

function toDate(val: any): Date {
    if (val instanceof Timestamp) return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val === "string" || typeof val === "number") return new Date(val);
    return new Date();
}

function logSnapshotError(scope: string) {
    return (error: Error) => {
        console.error(`Error loading ${scope}:`, error);
    };
}

function eventFromDoc(docSnap: any): EventType {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        createdAt: toDate(d.createdAt),
        updatedAt: toDate(d.updatedAt),
    } as EventType;
}

export function bookingFromDoc(docSnap: any): Booking {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        startTime: toDate(d.startTime),
        endTime: toDate(d.endTime),
        createdAt: toDate(d.createdAt),
        updatedAt: d.updatedAt ? toDate(d.updatedAt) : undefined,
        eventPreviousStartTime: d.eventPreviousStartTime ? toDate(d.eventPreviousStartTime) : undefined,
        eventChangeNotifiedAt: d.eventChangeNotifiedAt ? toDate(d.eventChangeNotifiedAt) : undefined,
        eventChangeRespondedAt: d.eventChangeRespondedAt ? toDate(d.eventChangeRespondedAt) : undefined,
    } as Booking;
}

// ─── Admins ───────────────────────────────────────────────────────────

const adminsCol = collection(db, "admins");
// Miroir de admins indexé par email : les règles Firestore ne savent lire un
// document que par son chemin, c'est lui qui donne les droits admin.
const adminAccessCol = collection(db, "admin_access");

function adminAccessRef(email: string) {
    return doc(adminAccessCol, email.trim().toLowerCase());
}

export async function getAdminByEmail(email: string): Promise<Admin | null> {
    const q = query(adminsCol, where("email", "==", email.toLowerCase()));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0].data();
    return {
        ...d,
        id: snap.docs[0].id,
        createdAt: toDate(d.createdAt)
    } as Admin;
}

export async function getAdmins(): Promise<Admin[]> {
    const snap = await getDocs(query(adminsCol, orderBy("createdAt", "desc")));
    return snap.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: toDate(doc.data().createdAt)
    } as Admin));
}

export async function deleteAdmin(id: string): Promise<void> {
    const adminSnap = await getDoc(doc(db, "admins", id));
    const email = adminSnap.data()?.email;
    // admin_access en dernier : il porte les droits nécessaires aux écritures précédentes.
    await deleteDoc(doc(db, "admins", id));
    if (typeof email === "string" && email) await deleteDoc(adminAccessRef(email));
}

export async function updateAdminRole(id: string, role: AdminRole): Promise<void> {
    const adminSnap = await getDoc(doc(db, "admins", id));
    const email = adminSnap.data()?.email;
    await updateDoc(doc(db, "admins", id), { role });
    if (typeof email === "string" && email) await setDoc(adminAccessRef(email), { email: email.toLowerCase(), role });
}

export async function createAdmin(email: string, name: string, role: AdminRole): Promise<string> {
    await setDoc(adminAccessRef(email), { email: email.trim().toLowerCase(), role });
    const docRef = await addDoc(adminsCol, {
        email: email.toLowerCase(),
        name,
        role,
        createdAt: Timestamp.now()
    });
    return docRef.id;
}

// ─── Event Types ────────────────────────────────────────────────────────

const eventsCol = collection(db, "eventTypes");

export async function getEventTypes(): Promise<EventType[]> {
    const snap = await getDocs(query(eventsCol, orderBy("createdAt", "desc")));
    return snap.docs.map(eventFromDoc);
}

export function subscribeEventTypes(callback: (events: EventType[]) => void): Unsubscribe {
    return onSnapshot(query(eventsCol, orderBy("createdAt", "desc")), (snap) => {
        callback(snap.docs.map(eventFromDoc));
    });
}

export async function getEventTypeById(id: string): Promise<EventType | null> {
    const docSnap = await getDoc(doc(db, "eventTypes", id));
    if (!docSnap.exists()) return null;
    return eventFromDoc(docSnap);
}

export async function getEventTypeBySlug(slug: string): Promise<EventType | null> {
    const snap = await getDocs(query(eventsCol, where("slug", "==", slug)));
    if (snap.empty) return null;
    return eventFromDoc(snap.docs[0]);
}

export async function createEventType(data: Omit<EventType, "id" | "createdAt" | "updatedAt">): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(eventsCol, {
        ...data,
        createdAt: now,
        updatedAt: now,
    });
    return docRef.id;
}

export async function updateEventType(id: string, data: Partial<EventType>): Promise<void> {
    const { id: _id, ...rest } = data as any;
    await updateDoc(doc(db, "eventTypes", id), {
        ...rest,
        updatedAt: Timestamp.now(),
    });
}

export async function deleteEventType(id: string): Promise<void> {
    await deleteDoc(doc(db, "eventTypes", id));
}

export type EventChangeMutationResult = {
    eventChangeId: string;
    affectedBookings: Booking[];
};

function createEventChangeId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function createManageToken(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID().replace(/-/g, "");
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function getMovedDateOverrides(event: EventType, sourceDate: string, targetDate: string) {
    const sourceOverride = (event.overrides || []).find((override) => override.date === sourceDate);
    const sourceWeekday = new Date(`${sourceDate}T12:00:00Z`).getUTCDay();
    const sourceRules = (event.availabilityRules || []).filter(
        (rule) => rule.enabled && rule.dayOfWeek === sourceWeekday,
    );

    const overrideTimeRanges = sourceOverride?.timeRanges?.length
        ? sourceOverride.timeRanges
        : sourceOverride?.startTime && sourceOverride?.endTime
            ? [{ start: sourceOverride.startTime, end: sourceOverride.endTime }]
            : [];
    const targetAvailability = sourceOverride?.available
        ? (overrideTimeRanges.length ? { timeRanges: overrideTimeRanges } : {})
        : sourceRules.length > 0
            ? {
                startTime: sourceRules[0].startTime,
                endTime: sourceRules[sourceRules.length - 1].endTime,
                timeRanges: sourceRules.map((rule) => ({ start: rule.startTime, end: rule.endTime })),
            }
            : {};

    return [
        ...(event.overrides || []).filter(
            (override) => override.date !== sourceDate && override.date !== targetDate,
        ),
        { date: sourceDate, available: false },
        { date: targetDate, available: true, ...targetAvailability },
    ];
}

/** Fetches every appointment attached to one event. The caller decides which dates/statuses are relevant. */
export async function getBookingsByEventType(eventTypeId: string): Promise<Booking[]> {
    const snap = await getDocs(query(bookingsCol, where("eventTypeId", "==", eventTypeId)));
    return snap.docs.map(bookingFromDoc);
}

/**
 * Moves one occurrence of an event and every active appointment on that date.
 * Appointment times are preserved in Europe/Paris and require a fresh client response.
 */
export async function moveEventTypeDate(
    event: EventType,
    sourceDate: string,
    targetDate: string,
    reason?: string,
): Promise<EventChangeMutationResult> {
    if (!sourceDate || !targetDate || sourceDate === targetDate) {
        throw new Error("Les dates de départ et d’arrivée doivent être différentes.");
    }

    const today = formatInParis(new Date(), "yyyy-MM-dd");
    if (sourceDate < today || targetDate < today) {
        throw new Error("Seules les dates actuelles ou à venir peuvent être déplacées.");
    }

    const snap = await getDocs(query(bookingsCol, where("eventTypeId", "==", event.id)));
    const bookingRecords = snap.docs.map((bookingDoc) => ({
        ref: bookingDoc.ref,
        booking: bookingFromDoc(bookingDoc),
    }));
    const now = new Date();
    const affectedRecords = bookingRecords.filter(({ booking }) => (
        booking.status !== "cancelled"
        && booking.startTime >= now
        && formatInParis(booking.startTime, "yyyy-MM-dd") === sourceDate
    ));
    const targetHasAppointments = bookingRecords.some(({ booking }) => (
        booking.status !== "cancelled"
        && booking.startTime >= now
        && formatInParis(booking.startTime, "yyyy-MM-dd") === targetDate
    ));

    if (targetHasAppointments) {
        throw new Error("La nouvelle date contient déjà des rendez-vous pour cet événement.");
    }

    const sourceOverride = (event.overrides || []).find((override) => override.date === sourceDate);
    const sourceWeekday = new Date(`${sourceDate}T12:00:00Z`).getUTCDay();
    const hasRegularAvailability = (event.availabilityRules || []).some(
        (rule) => rule.enabled && rule.dayOfWeek === sourceWeekday,
    );
    const sourceIsAvailable = sourceOverride ? sourceOverride.available : hasRegularAvailability;
    if (affectedRecords.length === 0 && !sourceIsAvailable) {
        throw new Error("Aucune occurrence ni aucun rendez-vous n’existe à la date de départ.");
    }

    const { writeBatch } = await import("firebase/firestore");
    const eventChangeId = createEventChangeId();
    const eventChangeReason = reason?.trim() || "Changement de date de l’événement";
    const movedBookings: Booking[] = [];
    const updates = affectedRecords.map(({ ref, booking }) => {
        const time = formatInParis(booking.startTime, "HH:mm");
        const newStartTime = parseInParis(`${targetDate}T${time}`);
        const durationMs = Math.max(0, booking.endTime.getTime() - booking.startTime.getTime());
        const newEndTime = new Date(newStartTime.getTime() + durationMs);
        const manageToken = booking.manageToken || createManageToken();
        const movedBooking: Booking = {
            ...booking,
            startTime: newStartTime,
            endTime: newEndTime,
            status: "pending",
            manageToken,
            reason: eventChangeReason,
            eventChangeId,
            eventChangeType: "moved",
            eventChangeResponse: "pending",
            eventPreviousStartTime: booking.startTime,
            eventChangeReason,
        };
        movedBookings.push(movedBooking);
        return { ref, booking: movedBooking };
    });

    if (event.overlapPolicy.mode === "global" && updates.length > 0) {
        const targetDayStart = parseInParis(`${targetDate}T00:00`);
        const targetDayEnd = parseInParis(`${targetDate}T23:59`);
        const targetSnapshot = await getDocs(query(
            bookingsCol,
            where("startTime", ">=", Timestamp.fromDate(targetDayStart)),
            where("startTime", "<=", Timestamp.fromDate(targetDayEnd)),
        ));
        const targetBookings = targetSnapshot.docs
            .map(bookingFromDoc)
            .filter((booking) => booking.status !== "cancelled" && !booking.isSansRendezVous);
        const hasGlobalConflict = updates.some(({ booking: movedBooking }) => (
            targetBookings.some((targetBooking) => (
                targetBooking.startTime < movedBooking.endTime
                && targetBooking.endTime > movedBooking.startTime
            ))
        ));
        if (hasGlobalConflict) {
            throw new Error("Au moins un horaire est déjà occupé par un autre événement à la nouvelle date.");
        }
    }

    // Firestore batches accept at most 500 operations. Keep room for the event update.
    const chunkSize = 450;
    const firstBatch = writeBatch(db);
    firstBatch.update(doc(db, "eventTypes", event.id), {
        overrides: getMovedDateOverrides(event, sourceDate, targetDate),
        updatedAt: Timestamp.now(),
        lastEventChangeId: eventChangeId,
        lastEventChangeAt: Timestamp.now(),
    });
    updates.slice(0, chunkSize).forEach(({ ref, booking }) => {
        firstBatch.update(ref, {
            startTime: Timestamp.fromDate(booking.startTime),
            endTime: Timestamp.fromDate(booking.endTime),
            status: booking.status,
            manageToken: booking.manageToken,
            reason: booking.reason,
            eventChangeId,
            eventChangeType: "moved",
            eventChangeResponse: "pending",
            eventPreviousStartTime: Timestamp.fromDate(booking.eventPreviousStartTime!),
            eventChangeReason,
            eventChangeNotifiedAt: deleteField(),
            eventChangeRespondedAt: deleteField(),
            updatedAt: Timestamp.now(),
        });
    });
    await firstBatch.commit();

    for (let i = chunkSize; i < updates.length; i += chunkSize) {
        const batch = writeBatch(db);
        updates.slice(i, i + chunkSize).forEach(({ ref, booking }) => {
            batch.update(ref, {
                startTime: Timestamp.fromDate(booking.startTime),
                endTime: Timestamp.fromDate(booking.endTime),
                status: booking.status,
                manageToken: booking.manageToken,
                reason: booking.reason,
                eventChangeId,
                eventChangeType: "moved",
                eventChangeResponse: "pending",
                eventPreviousStartTime: Timestamp.fromDate(booking.eventPreviousStartTime!),
                eventChangeReason,
                eventChangeNotifiedAt: deleteField(),
                eventChangeRespondedAt: deleteField(),
                updatedAt: Timestamp.now(),
            });
        });
        await batch.commit();
    }

    return { eventChangeId, affectedBookings: movedBookings };
}

/** Cancels every future active appointment before removing the event itself. */
export async function deleteEventTypeAndCancelBookings(
    event: EventType,
    reason?: string,
): Promise<EventChangeMutationResult> {
    const snap = await getDocs(query(bookingsCol, where("eventTypeId", "==", event.id)));
    const now = new Date();
    const affectedRecords = snap.docs
        .map((bookingDoc) => ({ ref: bookingDoc.ref, booking: bookingFromDoc(bookingDoc) }))
        .filter(({ booking }) => booking.status !== "cancelled" && booking.startTime >= now);
    const eventChangeId = createEventChangeId();
    const eventChangeReason = reason?.trim() || "Suppression de l’événement";
    const { writeBatch } = await import("firebase/firestore");
    const chunkSize = 450;

    for (let i = 0; i < affectedRecords.length; i += chunkSize) {
        const batch = writeBatch(db);
        affectedRecords.slice(i, i + chunkSize).forEach(({ ref, booking }) => {
            batch.update(ref, {
                status: "cancelled",
                reason: eventChangeReason,
                eventChangeId,
                eventChangeType: "deleted",
                eventChangeResponse: "cancelled",
                eventPreviousStartTime: Timestamp.fromDate(booking.startTime),
                eventChangeReason,
                eventChangeNotifiedAt: deleteField(),
                eventChangeRespondedAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            });
        });
        await batch.commit();
    }

    // Delete only after all appointments have safely been cancelled.
    await deleteDoc(doc(db, "eventTypes", event.id));
    return {
        eventChangeId,
        affectedBookings: affectedRecords.map(({ booking }) => ({
            ...booking,
            status: "cancelled",
            reason: eventChangeReason,
            eventChangeId,
            eventChangeType: "deleted",
            eventChangeResponse: "cancelled",
            eventPreviousStartTime: booking.startTime,
            eventChangeReason,
        })),
    };
}

export async function combineEvents(targetEventId: string, sourceEventIds: string[]): Promise<void> {
    const targetEvent = await getEventTypeById(targetEventId);
    if (!targetEvent) throw new Error("Événement cible non trouvé");

    const { writeBatch } = await import("firebase/firestore");
    
    // Find all bookings for these source events
    const bookingRefs: any[] = [];
    for (const sourceId of sourceEventIds) {
        const q = query(bookingsCol, where("eventTypeId", "==", sourceId));
        const snap = await getDocs(q);
        snap.docs.forEach((d) => {
            bookingRefs.push({ ref: d.ref, title: targetEvent.title });
        });
    }

    // Update bookings in chunks of 450
    const chunkSize = 450;
    for (let i = 0; i < bookingRefs.length; i += chunkSize) {
        const batch = writeBatch(db);
        const chunk = bookingRefs.slice(i, i + chunkSize);
        chunk.forEach((item) => {
            batch.update(item.ref, {
                eventTypeId: targetEventId,
                eventTitle: item.title,
                updatedAt: Timestamp.now(),
            });
        });
        await batch.commit();
    }

    // Delete/Deactivate the source events
    const deleteBatch = writeBatch(db);
    for (const sourceId of sourceEventIds) {
        deleteBatch.delete(doc(db, "eventTypes", sourceId));
    }
    await deleteBatch.commit();
}

// ─── Bookings ───────────────────────────────────────────────────────────

const bookingsCol = collection(db, "bookings");
const clientsCol = collection(db, "clients");
const RECENT_BOOKINGS_LOOKBACK_DAYS = 14;
const RECENT_BOOKINGS_LIMIT = 150;
// Claims and sales can contain base64 PDFs, so keep live listeners deliberately small.
const RECENT_FINANCIAL_RECORDS_LIMIT = 500;
const RECENT_INVENTORY_LIMIT = 1000;
const RECENT_CLIENTS_LIMIT = 1000;
const CLIENT_HISTORY_LIMIT = 250;

function daysAgoStart(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setHours(0, 0, 0, 0);
    return date;
}

function recentBookingsQuery(maxResults = RECENT_BOOKINGS_LIMIT) {
    return query(
        bookingsCol,
        where("startTime", ">=", Timestamp.fromDate(daysAgoStart(RECENT_BOOKINGS_LOOKBACK_DAYS))),
        orderBy("startTime", "desc"),
        firestoreLimit(maxResults),
    );
}

export async function getBookings(): Promise<Booking[]> {
    const snap = await getDocs(recentBookingsQuery());
    return snap.docs.map(bookingFromDoc);
}

export async function getUpcomingBookings(maxResults = 250): Promise<Booking[]> {
    const snap = await getDocs(query(
        bookingsCol,
        where("startTime", ">=", Timestamp.fromDate(startOfDay(new Date()))),
        orderBy("startTime", "asc"),
        firestoreLimit(maxResults),
    ));
    return snap.docs.map(bookingFromDoc);
}

export function subscribeBookings(
    callback: (bookings: Booking[]) => void,
    onError?: (error: Error) => void,
    dateRange?: { from?: Date; to?: Date }
): Unsubscribe {
    let q = query(bookingsCol, orderBy("startTime", "desc"));
    if (dateRange && dateRange.from) {
        const start = new Date(dateRange.from);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.to || dateRange.from);
        end.setHours(23, 59, 59, 999);
        q = query(
            bookingsCol,
            where("startTime", ">=", Timestamp.fromDate(start)),
            where("startTime", "<=", Timestamp.fromDate(end)),
            orderBy("startTime", "desc")
        );
    } else {
        q = recentBookingsQuery();
    }
    return onSnapshot(
        q,
        (snap) => {
            callback(snap.docs.map(bookingFromDoc));
        },
        onError || logSnapshotError("bookings"),
    );
}

export function subscribeArchivedBookings(
    callback: (bookings: Booking[]) => void,
    onError?: (error: Error) => void,
    maxResults = 2500,
): Unsubscribe {
    return onSnapshot(
        query(
            bookingsCol,
            where("startTime", "<", Timestamp.now()),
            orderBy("startTime", "desc"),
            firestoreLimit(maxResults),
        ),
        (snap) => callback(snap.docs.map(bookingFromDoc)),
        onError || logSnapshotError("archived bookings"),
    );
}

export async function createBooking(data: Omit<Booking, "id" | "createdAt">): Promise<{ id: string; manageToken: string }> {
    const now = Timestamp.now();
    const manageToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    // Check if client exists
    let clientId = data.clientId;
    const bookingDetails = parseBookingPayload(data.visitorPhone || "");
    const phoneToSave = bookingDetails.phone;
    const addressPatch: Partial<Pick<Client, "address" | "zipCode" | "city">> = {};
    if (bookingDetails.address) addressPatch.address = bookingDetails.address;
    if (bookingDetails.zipCode) addressPatch.zipCode = bookingDetails.zipCode;
    if (bookingDetails.city) addressPatch.city = bookingDetails.city;

    if (!clientId) {
        // Search by email
        const clientQuery = await getDocs(query(clientsCol, where("email", "==", data.visitorEmail)));
        if (!clientQuery.empty) {
            clientId = clientQuery.docs[0].id;
            // Update client stats
            const clientDoc = clientQuery.docs[0];
            const currentTotal = clientDoc.data().totalBookings || 0;
            await updateDoc(doc(db, "clients", clientId), {
                totalBookings: currentTotal + 1,
                lastBookingDate: Timestamp.fromDate(data.startTime instanceof Date ? data.startTime : new Date(data.startTime)),
                updatedAt: now,
                phone: phoneToSave || clientDoc.data().phone, // update phone if provided
                ...addressPatch,
            });
        } else {
            // Create new client
            const newClientRef = await addDoc(clientsCol, {
                name: data.visitorName,
                email: data.visitorEmail,
                phone: phoneToSave,
                ...addressPatch,
                totalBookings: 1,
                lastBookingDate: Timestamp.fromDate(data.startTime instanceof Date ? data.startTime : new Date(data.startTime)),
                createdAt: now,
                updatedAt: now,
            });
            clientId = newClientRef.id;
        }
    }

    const docRef = await addDoc(bookingsCol, {
        ...data,
        clientId,
        manageToken,
        startTime: Timestamp.fromDate(data.startTime instanceof Date ? data.startTime : new Date(data.startTime)),
        endTime: Timestamp.fromDate(data.endTime instanceof Date ? data.endTime : new Date(data.endTime)),
        createdAt: now,
    });
    
    return { id: docRef.id, manageToken };
}

export async function updateBookingStatus(id: string, status: Booking["status"], reason?: string): Promise<void> {
    await updateDoc(doc(db, "bookings", id), { 
        status,
        ...(reason && { reason }),
        updatedAt: Timestamp.now()
    });
}

export async function respondToEventChange(id: string, response: Exclude<EventChangeResponse, "pending">): Promise<void> {
    await updateDoc(doc(db, "bookings", id), {
        status: response === "confirmed" ? "confirmed" : "cancelled",
        eventChangeResponse: response,
        eventChangeRespondedAt: Timestamp.now(),
        ...(response === "cancelled" ? { reason: "Nouvelle date refusée par le client" } : {}),
        updatedAt: Timestamp.now(),
    });
}

export async function updateBookingTime(id: string, startTime: Date, endTime: Date, reason?: string): Promise<void> {
    await updateDoc(doc(db, "bookings", id), {
        startTime: Timestamp.fromDate(startTime),
        endTime: Timestamp.fromDate(endTime),
        ...(reason && { reason }),
        updatedAt: Timestamp.now(),
    });
}

export async function updateBookingAttendance(id: string, attendance: "attended" | "no_show"): Promise<void> {
    await updateDoc(doc(db, "bookings", id), { attendance });
}

export async function getBookingByToken(token: string): Promise<Booking | null> {
    const q = query(bookingsCol, where("manageToken", "==", token));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return bookingFromDoc(snap.docs[0]);
}

export async function updateBooking(id: string, data: Partial<Booking>): Promise<void> {
    const { id: _id, createdAt: _ca, ...rest } = data as any;
    // Map dates back to Timestamps if they are Date objects
    if (rest.startTime instanceof Date) rest.startTime = Timestamp.fromDate(rest.startTime);
    if (rest.endTime instanceof Date) rest.endTime = Timestamp.fromDate(rest.endTime);
    if (rest.eventPreviousStartTime instanceof Date) rest.eventPreviousStartTime = Timestamp.fromDate(rest.eventPreviousStartTime);
    if (rest.eventChangeNotifiedAt instanceof Date) rest.eventChangeNotifiedAt = Timestamp.fromDate(rest.eventChangeNotifiedAt);
    if (rest.eventChangeRespondedAt instanceof Date) rest.eventChangeRespondedAt = Timestamp.fromDate(rest.eventChangeRespondedAt);
    
    await updateDoc(doc(db, "bookings", id), {
        ...rest,
        updatedAt: Timestamp.now()
    });
}

// ─── Reminders ──────────────────────────────────────────────────────────

const remindersCol = collection(db, "reminders");

export async function createReminder(data: { eventId: string; email: string; targetDate: Date }): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(remindersCol, {
        ...data,
        targetDate: Timestamp.fromDate(data.targetDate),
        createdAt: now,
        sent: false,
    });
    return docRef.id;
}

// ─── QualiRepar Claims ────────────────────────────────────────────────

const claimsCol = collection(db, "qualirepar_claims");

// Dernier statut lu chez Agora, enregistré pour ne pas réinterroger Agora à chaque affichage.
export interface StoredAgoraStatus {
    label: string;
    responseStatus?: string;
    checkedAt: Date;
}

export interface QualiReparClaim {
    id?: string;
    claimId: string;
    bookingId?: string | null;
    eventTypeId?: string | null;
    eventTitle?: string | null;
    date: Date;
    consumer: {
        firstName: string;
        lastName: string;
        phone: string;
        email: string;
        address?: string;
        zipCode?: string;
        city?: string;
    };
    consumerEmailNormalized?: string;
    product: {
        brand: string;
        equipment: string;
        model?: string;
        serialNumber: string;
        serialNumber2?: string;
        faultType: string;
        faultType2?: string;
        repairCode?: string;
        repairCode2?: string;
        irisSymptom?: string;
        irisSymptom2?: string;
        processedImeiCount?: 1 | 2;
    };
    invoiceNumber?: string;
    quoteNumber?: string;
    repairSiteId?: string;
    repairSiteName?: string;
    bonusAmount: number;
    totalAmount: number;
    purchasePrice?: number;
    extraRepairs?: { name: string; price: number }[];
    pdfUrls?: {
      attestation?: string;
      invoice?: string;
    };
    archivedAssets?: {
        signature?: StoredQualiReparAsset;
        nameplate?: StoredQualiReparAsset;
        attestation?: StoredQualiReparAsset;
        invoice?: StoredQualiReparAsset;
    };
    agoraStatus?: StoredAgoraStatus;
    createdAt: Date;
}

function claimFromDoc(docSnap: any): QualiReparClaim {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        date: toDate(d.date),
        createdAt: toDate(d.createdAt),
        ...(d.agoraStatus ? { agoraStatus: { ...d.agoraStatus, checkedAt: toDate(d.agoraStatus.checkedAt) } } : {}),
    } as QualiReparClaim;
}

export async function createQualiReparClaim(data: QualiReparClaim): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(claimsCol, {
        ...data,
        consumerEmailNormalized: data.consumer.email.toLowerCase().trim(),
        date: Timestamp.fromDate(data.date),
        createdAt: now,
    });

    // Also update/create client with address info for future auto-fill
    try {
        const clientEmail = data.consumer.email.toLowerCase().trim();
        const q = query(clientsCol, where("email", "==", clientEmail));
        const snap = await getDocs(q);
        
        if (!snap.empty) {
            await updateDoc(doc(db, "clients", snap.docs[0].id), {
                address: data.consumer.address || "",
                zipCode: data.consumer.zipCode || "",
                city: data.consumer.city || "",
                updatedAt: now
            });
        }
    } catch (e) {
        console.error("Failed to update client with claim address:", e);
    }

    return docRef.id;
}

export async function updateQualiReparClaim(id: string, data: Partial<QualiReparClaim>): Promise<void> {
    const { id: _id, createdAt: _ca, ...rest } = data as any;
    await updateDoc(doc(db, "qualirepar_claims", id), {
        ...rest,
        updatedAt: Timestamp.now()
    });
}

export async function getQualiReparClaims(): Promise<QualiReparClaim[]> {
    const snap = await getDocs(query(claimsCol, orderBy("date", "desc"), firestoreLimit(RECENT_FINANCIAL_RECORDS_LIMIT)));
    return snap.docs.map(claimFromDoc);
}

export async function getQualiReparClaimsByConsumerEmail(email: string): Promise<QualiReparClaim[]> {
    const normalizedEmail = email.toLowerCase().trim();
    if (!normalizedEmail) return [];

    const snap = await getDocs(
        query(
            claimsCol,
            where("consumerEmailNormalized", "==", normalizedEmail),
            firestoreLimit(CLIENT_HISTORY_LIMIT),
        ),
    );
    const legacySnap = await getDocs(
        query(
            claimsCol,
            where("consumer.email", "==", normalizedEmail),
            firestoreLimit(CLIENT_HISTORY_LIMIT),
        ),
    );
    const docsById = new Map([...snap.docs, ...legacySnap.docs].map(doc => [doc.id, doc]));

    if (docsById.size === 0) {
        return (await getQualiReparClaims())
            .filter(claim => claim.consumer.email.toLowerCase().trim() === normalizedEmail)
            .slice(0, CLIENT_HISTORY_LIMIT);
    }

    return Array.from(docsById.values())
        .map(claimFromDoc)
        .sort((a, b) => b.date.getTime() - a.date.getTime());
}

export function subscribeQualiReparClaims(
    callback: (claims: QualiReparClaim[]) => void,
    onError?: (error: Error) => void,
    dateRange?: { from?: Date; to?: Date },
    limitCount: number = RECENT_FINANCIAL_RECORDS_LIMIT
): Unsubscribe {
    let q = query(claimsCol, orderBy("date", "desc"));
    if (dateRange && dateRange.from) {
        const start = new Date(dateRange.from);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.to || dateRange.from);
        end.setHours(23, 59, 59, 999);
        q = query(
            claimsCol,
            where("date", ">=", Timestamp.fromDate(start)),
            where("date", "<=", Timestamp.fromDate(end)),
            orderBy("date", "desc")
        );
    } else {
        q = query(q, firestoreLimit(limitCount));
    }
    return onSnapshot(
        q,
        (snap) => {
            callback(snap.docs.map(claimFromDoc));
        },
        onError || logSnapshotError("QualiRepar claims"),
    );
}

// One-time paginated fetch of every QualiRepar claim, oldest history included.
// Reads in bounded batches (instead of a single unbounded query or an ever-growing
// realtime listener) so the UI can show progress and the read stays predictable in size.
export async function fetchAllQualiReparClaims(
    onProgress?: (loadedSoFar: number) => void,
    batchSize = 500
): Promise<QualiReparClaim[]> {
    const all: QualiReparClaim[] = [];
    let lastDoc: any = undefined;

    while (true) {
        const constraints = lastDoc
            ? [orderBy("date", "desc"), startAfter(lastDoc), firestoreLimit(batchSize)]
            : [orderBy("date", "desc"), firestoreLimit(batchSize)];
        const snap = await getDocs(query(claimsCol, ...constraints));
        if (snap.empty) break;

        all.push(...snap.docs.map(claimFromDoc));
        lastDoc = snap.docs[snap.docs.length - 1];
        onProgress?.(all.length);

        if (snap.docs.length < batchSize) break;
    }

    return all;
}

// ─── QualiRepar Legacy Counts ──────────────────────────────────────────

const legacyCol = collection(db, "qualirepar_legacy_counts");

export interface QualiReparLegacyCount {
    id?: string;
    month: string; // "YYYY-MM"
    siteId: "auchan" | "stand";
    count: number;
    system: "ancien_systeme";
    createdAt: Date;
}

function legacyCountFromDoc(docSnap: any): QualiReparLegacyCount {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        createdAt: toDate(d.createdAt),
    } as QualiReparLegacyCount;
}

export async function setQualiReparLegacyCount(month: string, siteId: "auchan" | "stand", count: number): Promise<void> {
    const docId = `${month}_${siteId}`;
    const docRef = doc(legacyCol, docId);
    await setDoc(docRef, {
        month,
        siteId,
        count,
        system: "ancien_systeme",
        createdAt: Timestamp.now()
    });
}

export async function deleteQualiReparLegacyCount(id: string): Promise<void> {
    await deleteDoc(doc(legacyCol, id));
}

export function subscribeQualiReparLegacyCounts(
    callback: (counts: QualiReparLegacyCount[]) => void,
    onError?: (error: Error) => void
): Unsubscribe {
    const q = query(legacyCol, orderBy("month", "desc"));
    return onSnapshot(
        q,
        (snap) => {
            callback(snap.docs.map(legacyCountFromDoc));
        },
        onError || logSnapshotError("QualiRepar legacy counts")
    );
}


export async function hasQualiReparClaimForSerialNumber(serialNumber: string): Promise<boolean> {
    const normalizedSerialNumber = serialNumber.trim();
    if (!normalizedSerialNumber) return false;

    const snap = await getDocs(query(claimsCol, where("product.serialNumber", "==", normalizedSerialNumber)));
    return !snap.empty;
}

// ─── QualiRepar Pending Claims ────────────────────────────────────────
// Dossier créé chez Agora mais pas encore finalisé (pièces jointes, soumission,
// enregistrement). Supprimé dès que le dossier arrive dans qualirepar_claims :
// s'il reste, la finalisation a échoué et le dossier Agora peut être repris.

const pendingClaimsCol = collection(db, "qualirepar_pending_claims");

export interface PendingQualiReparClaim {
    claimId: string;
    serialNumber: string; // normalisé (normalizeQualiReparSerialNumber)
    consumer: { firstName: string; lastName: string; phone: string; email: string };
    repairSiteId?: string;
    repairSiteName?: string;
    // Vraies pièces sauvegardées dès le début de la finalisation.
    signature?: StoredQualiReparAsset;
    nameplate?: StoredQualiReparAsset;
    lastError?: string | null;
    lastErrorStep?: string;
    lastErrorContext?: { userAgent: string; nameplate: string };
    createdAt: Date;
    updatedAt: Date;
}

export async function savePendingQualiReparClaim(
    data: Omit<PendingQualiReparClaim, "lastError" | "createdAt" | "updatedAt">,
): Promise<void> {
    const now = Timestamp.now();
    await setDoc(doc(pendingClaimsCol, data.claimId), { ...data, lastError: null, createdAt: now, updatedAt: now });
}

export async function savePendingQualiReparClaimAssets(
    claimId: string,
    assets: Pick<PendingQualiReparClaim, "signature" | "nameplate">,
): Promise<void> {
    await updateDoc(doc(pendingClaimsCol, claimId), { ...assets, updatedAt: Timestamp.now() });
}

export async function markPendingQualiReparClaimFailed(
    claimId: string,
    failure: { error: string; step: string; userAgent: string; nameplate: string },
): Promise<void> {
    await updateDoc(doc(pendingClaimsCol, claimId), {
        lastError: failure.error,
        lastErrorStep: failure.step,
        lastErrorContext: { userAgent: failure.userAgent, nameplate: failure.nameplate },
        updatedAt: Timestamp.now(),
    });
}

export async function deletePendingQualiReparClaim(claimId: string): Promise<void> {
    await deleteDoc(doc(pendingClaimsCol, claimId));
}

export async function findPendingQualiReparClaim(normalizedSerialNumber: string): Promise<PendingQualiReparClaim | null> {
    if (!normalizedSerialNumber) return null;

    const snap = await getDocs(query(pendingClaimsCol, where("serialNumber", "==", normalizedSerialNumber), firestoreLimit(1)));
    if (snap.empty) return null;

    const d = snap.docs[0].data();
    return { ...d, createdAt: toDate(d.createdAt), updatedAt: toDate(d.updatedAt) } as PendingQualiReparClaim;
}

export async function deleteQualiReparClaim(id: string): Promise<void> {
    const claimRef = doc(db, "qualirepar_claims", id);
    const claimSnap = await getDoc(claimRef);

    if (claimSnap.exists()) {
        const claim = claimSnap.data() as QualiReparClaim;
        await deleteQualiReparAsset(claim.archivedAssets?.signature?.path);
        await deleteQualiReparAsset(claim.archivedAssets?.nameplate?.path);
        await deleteQualiReparAsset(claim.archivedAssets?.attestation?.path);
        await deleteQualiReparAsset(claim.archivedAssets?.invoice?.path);
    }

    await deleteDoc(claimRef);
}

export async function searchClientByName(name: string): Promise<Client[]> {
    const q = query(
        clientsCol,
        where("name", ">=", name),
        where("name", "<=", name + '\uf8ff'),
        firestoreLimit(RECENT_CLIENTS_LIMIT),
    );
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
}

export async function getClients(): Promise<Client[]> {
    const snap = await getDocs(query(clientsCol, orderBy("name", "asc"), firestoreLimit(RECENT_CLIENTS_LIMIT)));
    return snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
}

// ─── Stats & TimeSlots ──────────────────────────────────────────────────

export function getStatsFromBookings(bookings: Booking[]) {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
        thisWeek: bookings.filter(b => b.startTime >= startOfWeek && b.status !== 'cancelled').length,
        thisMonth: bookings.filter(b => b.startTime >= startOfMonth && b.status !== 'cancelled').length,
        upcoming: bookings.filter(b => b.startTime >= now && b.status !== 'cancelled').length,
        cancelled: bookings.filter(b => b.status === 'cancelled').length,
    };
}

export function generateTimeSlots(date: Date, event: EventType, existingBookings: Booking[], includePast = false): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const dateStr = formatInParis(date, "yyyy-MM-dd");
    const [y, m, dayVal] = dateStr.split("-").map(Number);
    const localParisDate = new Date(y, m - 1, dayVal);
    const bookingsToConsider = existingBookings.filter((bk) => {
        if (bk.status === "cancelled") return false;
        if (bk.isSansRendezVous) return false;
        if (event.overlapPolicy.mode === "global") return true;
        return bk.eventTypeId === event.id;
    });

    // 1. Get availability rules for this day
    const dayOfWeek = localParisDate.getDay();
    const rule = event.availabilityRules?.find((r) => r.dayOfWeek === dayOfWeek);
    
    // 2. Check for overrides
    const override = event.overrides?.find((o) => o.date === dateStr);
    
    // If specifically marked as unavailable in override, return empty
    if (override && !override.available) return [];
    
    // Use override ranges if present, else use rule ranges if enabled, else empty
    const activeRanges = override?.timeRanges || (rule?.enabled ? [{ start: rule.startTime, end: rule.endTime }] : []);

    if (!activeRanges || activeRanges.length === 0) return [];

    activeRanges.forEach((range) => {
        const [startH, startM] = range.start.split(":").map(Number);
        const [endH, endM] = range.end.split(":").map(Number);

        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;

        for (let m = startMinutes; m + event.duration <= endMinutes; m += event.slotInterval) {
            const h = Math.floor(m / 60);
            const min = m % 60;
            const time = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;

            const slotStart = parseInParis(`${dateStr}T${time}`);
            const slotEnd = new Date(slotStart.getTime() + event.duration * 60 * 1000);

            // Hide past slots if today in Paris
            const todayStr = formatInParis(new Date(), "yyyy-MM-dd");
            if (!includePast && dateStr === todayStr && isBefore(slotStart, new Date())) continue;

            let isBooked = false;

            if (event.overlapPolicy.mode === "capacity" && event.overlapPolicy.capacity) {
                const overlapping = bookingsToConsider.filter((bk) => {
                    return bk.startTime < slotEnd && bk.endTime > slotStart;
                }).length;
                isBooked = overlapping >= event.overlapPolicy.capacity;
            } else {
                isBooked = bookingsToConsider.some((bk) => {
                    return bk.startTime < slotEnd && bk.endTime > slotStart;
                });
            }

            if (!slots.find(s => s.time === time)) {
                slots.push({ time, available: !isBooked });
            }
        }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
}

export async function updateQualiReparClaimStatus(claimId: string, status: StoredAgoraStatus) {
    await updateDoc(doc(db, "qualirepar_claims", claimId), {
        agoraStatus: { ...status, checkedAt: Timestamp.fromDate(status.checkedAt) },
        updatedAt: serverTimestamp(),
    });
}

export async function updateClaimRepairSite(claimId: string, siteId: string, siteName: string) {
    const claimRef = doc(db, "qualirepar_claims", claimId);
    await updateDoc(claimRef, {
        repairSiteId: siteId,
        repairSiteName: siteName,
        updatedAt: serverTimestamp()
    });
}

// ─── Sales ───────────────────────────────────────────────────────────

const salesCol = collection(db, "sales");

export interface Sale {
    id?: string;
    saleId: string;
    bookingId?: string | null;
    sumupTransactionCode?: string;
    sumupTransactionId?: string;
    sumupAmount?: number;
    sumupCurrency?: string;
    sumupLinkedAt?: Date;
    date: Date;
    type?: "vente" | "reparation";
    consumer: {
        firstName: string;
        lastName: string;
        phone: string;
        email: string;
        address: string;
        zipCode: string;
        city: string;
        country: string;
    };
    product: {
        eventTitle: string;
        imei: string;
        model: string;
        price: number;
        purchasePrice?: number; // Price paid for the item (PA)
        vatScheme?: VatScheme;
        repairItems?: { name: string; price: number }[]; // For repairs
        brand?: string;
        color?: string;
        memory?: string;
        stockType?: string;
        status?: string;
        warranty?: string;
    };
    siteId?: string;
    siteName?: string;
    pdfUrl?: string;
    refund?: {
        status: "pending" | "refunded";
        refundedAt: Date;
        refundedAmount: number;
        reason?: string;
        creditNoteId: string;
        creditNotePdfUrl?: string;
        sentToEmail?: string;
        sentAt?: Date;
        sumupRefunded?: boolean;
        restocked?: boolean;
    };
    createdAt: Date;
}

function saleFromDoc(docSnap: any): Sale {
    const d = docSnap.data();
    return {
        ...d,
        id: docSnap.id,
        date: toDate(d.date),
        refund: d.refund ? {
            ...d.refund,
            refundedAt: toDate(d.refund.refundedAt),
            sentAt: d.refund.sentAt ? toDate(d.refund.sentAt) : undefined,
        } : undefined,
        createdAt: toDate(d.createdAt),
    } as Sale;
}

export async function createSale(data: Sale): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(salesCol, {
        ...data,
        date: Timestamp.fromDate(data.date),
        createdAt: now,
    });
    return docRef.id;
}

export async function getSaleByImei(imei: string): Promise<Sale | null> {
    if (!imei) return null;
    const q = query(salesCol, where("product.imei", "==", imei.trim()), firestoreLimit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return saleFromDoc(snap.docs[0]);
}

export async function updateSale(id: string, data: any): Promise<void> {
    await updateDoc(doc(db, "sales", id), {
        ...data,
        updatedAt: Timestamp.now()
    });
}

export async function getSales(): Promise<Sale[]> {
    const snap = await getDocs(query(salesCol, orderBy("date", "desc"), firestoreLimit(RECENT_FINANCIAL_RECORDS_LIMIT)));
    return snap.docs.map(saleFromDoc);
}

export function subscribeSales(
    callback: (sales: Sale[]) => void,
    onError?: (error: Error) => void,
    dateRange?: { from?: Date; to?: Date }
): Unsubscribe {
    let q = query(salesCol, orderBy("date", "desc"));
    if (dateRange && dateRange.from) {
        const start = new Date(dateRange.from);
        start.setHours(0, 0, 0, 0);
        const end = new Date(dateRange.to || dateRange.from);
        end.setHours(23, 59, 59, 999);
        q = query(
            salesCol,
            where("date", ">=", Timestamp.fromDate(start)),
            where("date", "<=", Timestamp.fromDate(end)),
            orderBy("date", "desc")
        );
    } else {
        q = query(q, firestoreLimit(RECENT_FINANCIAL_RECORDS_LIMIT));
    }
    return onSnapshot(
        q,
        (snap) => {
            callback(snap.docs.map(saleFromDoc));
        },
        onError || logSnapshotError("sales"),
    );
}

export async function deleteSale(id: string): Promise<void> {
    await deleteDoc(doc(db, "sales", id));
}

// ─── Inventory / Stock ───────────────────────────────────────────────

const inventoryCol = collection(db, "inventory");

export interface InventoryItem {
    id?: string;
    imei: string;
    brand: string;
    model: string;
    grade: string;
    color: string;
    memory: string;
    stockType: string;
    status: string;
    purchasePrice: number;
    priceHt: number;
    priceTtc: number;
    warranty: string;
    vatScheme: VatScheme;
    createdAt: Date;
    updatedAt: Date;
    transactionCode?: string | null;
}

export async function addInventoryItems(items: Omit<InventoryItem, "id" | "createdAt" | "updatedAt">[]): Promise<void> {
    const now = Timestamp.now();
    for (const item of items) {
        // Search if IMEI already exists to avoid duplicates
        const q = query(inventoryCol, where("imei", "==", String(item.imei).trim()));
        const snap = await getDocs(q);
        if (snap.empty) {
            await addDoc(inventoryCol, {
                ...item,
                createdAt: now,
                updatedAt: now
            });
        } else {
            // Update existing? We stick to "add only new" or "update if exists"
            await updateDoc(doc(db, "inventory", snap.docs[0].id), {
                ...item,
                updatedAt: now
            });
        }
    }
}

export async function getInventoryItemByImei(imei: string): Promise<InventoryItem | null> {
    const q = query(inventoryCol, where("imei", "==", imei.trim()));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0].data();
    return {
        ...d,
        grade: typeof d.grade === "string" ? d.grade : "",
        id: snap.docs[0].id,
        createdAt: toDate(d.createdAt),
        updatedAt: toDate(d.updatedAt)
    } as InventoryItem;
}

export function subscribeInventory(
    callback: (items: InventoryItem[]) => void,
    onError?: (error: Error) => void,
): Unsubscribe {
    return onSnapshot(
        query(inventoryCol, orderBy("updatedAt", "desc"), firestoreLimit(RECENT_INVENTORY_LIMIT)),
        (snap) => {
            callback(snap.docs.map(doc => ({
                ...doc.data(),
                grade: typeof doc.data().grade === "string" ? doc.data().grade : "",
                id: doc.id,
                createdAt: toDate(doc.data().createdAt),
                updatedAt: toDate(doc.data().updatedAt)
            } as InventoryItem)));
        },
        onError || logSnapshotError("inventory"),
    );
}

export async function markInventoryAsSold(imei: string): Promise<void> {
    const q = query(inventoryCol, where("imei", "==", imei.trim()));
    const snap = await getDocs(q);
    if (!snap.empty) {
        await updateDoc(doc(db, "inventory", snap.docs[0].id), {
            status: "Vendu",
            updatedAt: Timestamp.now()
        });
    }
}

export async function updateInventoryItem(id: string, data: Partial<InventoryItem>): Promise<void> {
    const { id: _id, createdAt: _ca, ...rest } = data as any;
    await updateDoc(doc(db, "inventory", id), {
        ...rest,
        updatedAt: Timestamp.now()
    });
}

export async function deleteInventoryItem(id: string): Promise<void> {
    await deleteDoc(doc(db, "inventory", id));
}

export async function deleteInventoryItems(ids: string[]): Promise<void> {
    const { writeBatch } = await import("firebase/firestore");
    const batch = writeBatch(db);
    for (const id of ids) {
        batch.delete(doc(db, "inventory", id));
    }
    await batch.commit();
}

// ─── App Settings ──────────────────────────────────────────────────────

const settingsDoc = doc(db, "settings", "general");
let cachedSettings: AppSettings | null = null;

export async function getSettings(bypassCache = false): Promise<AppSettings> {
    if (!bypassCache && cachedSettings) {
        return cachedSettings;
    }
    const snap = await getDoc(settingsDoc);
    if (!snap.exists()) {
        const defaultSettings = { profileURL: "", updatedAt: new Date() };
        cachedSettings = defaultSettings;
        return defaultSettings;
    }
    const d = snap.data();
    const settings = {
        ...d,
        updatedAt: toDate(d.updatedAt)
    } as AppSettings;
    cachedSettings = settings;
    return settings;
}

export async function updateSettings(data: Partial<AppSettings>): Promise<void> {
    await setDoc(settingsDoc, {
        ...data,
        updatedAt: Timestamp.now()
    }, { merge: true });
    cachedSettings = null;
}

// ─── Email Templates ─────────────────────────────────────────────────────

const emailTemplatesDoc = doc(db, "settings", "emailTemplates");

export async function getEmailTemplateOverrides(): Promise<EmailTemplateOverrides> {
    const snap = await getDoc(emailTemplatesDoc);
    return snap.exists() ? (snap.data() as EmailTemplateOverrides) : {};
}

export async function saveEmailTemplateOverride(key: EmailTemplateKey, content: EmailTemplateContent): Promise<void> {
    await setDoc(emailTemplatesDoc, {
        [key]: { subject: content.subject, html: content.html, updatedAt: Timestamp.now() }
    }, { merge: true });
}

export async function resetEmailTemplateOverride(key: EmailTemplateKey): Promise<void> {
    await setDoc(emailTemplatesDoc, { [key]: deleteField() }, { merge: true });
}

// ─── Financial Settings ──────────────────────────────────────────────────

export interface FinancialSettings {
    marginRates?: Record<string, string>;
    sumupCostSettings?: Record<string, string>;
    qualiReparMarginRate?: string;
    operationStaffBaseRate?: number;
    operationStaffPerClaimRate?: number;
    updatedAt?: Date;
}

const financialSettingsDoc = doc(db, "settings", "financial");

export async function getFinancialSettings(): Promise<FinancialSettings> {
    const snap = await getDoc(financialSettingsDoc);
    if (!snap.exists()) {
        return {};
    }
    const d = snap.data();
    return {
        ...d,
        updatedAt: d.updatedAt ? toDate(d.updatedAt) : undefined
    };
}

export async function updateFinancialSettings(data: Partial<FinancialSettings>): Promise<void> {
    await setDoc(financialSettingsDoc, {
        ...data,
        updatedAt: Timestamp.now()
    }, { merge: true });
}

export function subscribeFinancialSettings(onUpdate: (data: FinancialSettings) => void): Unsubscribe {
    return onSnapshot(financialSettingsDoc, (snap) => {
        if (!snap.exists()) {
            onUpdate({});
            return;
        }
        const d = snap.data();
        onUpdate({
            ...d,
            updatedAt: d.updatedAt ? toDate(d.updatedAt) : undefined
        });
    });
}

// ─── Operation Staffing ────────────────────────────────────────────────

// Intervenants saisis à la main par opération (magasin + date), pour déduire
// leur rémunération de la marge. Clé : `${siteId}__${yyyy-MM-dd}`.
export type OperationStaffing = {
    technicians: number;
    animators: number;
    updatedAt?: Date;
};

const operationStaffingDoc = doc(db, "settings", "operation_staffing");

export function subscribeOperationStaffing(
    callback: (staffing: Record<string, OperationStaffing>) => void,
    onError?: (error: Error) => void,
): Unsubscribe {
    return onSnapshot(
        operationStaffingDoc,
        (snap) => {
            const entries = (snap.exists() ? snap.data().entries : null) || {};
            const staffing: Record<string, OperationStaffing> = {};
            Object.entries(entries as Record<string, any>).forEach(([key, value]) => {
                if (!value) return;
                staffing[key] = {
                    technicians: Number(value.technicians) || 0,
                    animators: Number(value.animators) || 0,
                    ...(value.updatedAt && { updatedAt: toDate(value.updatedAt) }),
                };
            });
            callback(staffing);
        },
        onError || logSnapshotError("operation staffing"),
    );
}

export async function saveOperationStaffing(key: string, staffing: Omit<OperationStaffing, "updatedAt">): Promise<void> {
    await setDoc(operationStaffingDoc, {
        entries: {
            [key]: {
                technicians: staffing.technicians,
                animators: staffing.animators,
                updatedAt: Timestamp.now(),
            },
        },
    }, { merge: true });
}

export async function deleteOperationStaffing(key: string): Promise<void> {
    await setDoc(operationStaffingDoc, { entries: { [key]: deleteField() } }, { merge: true });
}

// ─── Financial Transaction Classifications ─────────────────────────────

export type TransactionClassificationOverride = {
    id: string;
    category?: string;
    articleCategory?: string;
    manualImei?: string;
    purchasePrice?: number | null;
    excluded?: boolean;
    updatedAt?: Date;
};

const transactionClassificationsCol = collection(db, "transaction_classifications");

function transactionClassificationFromDoc(docSnap: any): TransactionClassificationOverride {
    const d = docSnap.data();
    return {
        id: docSnap.id,
        ...(typeof d.category === "string" && { category: d.category }),
        ...(typeof d.articleCategory === "string" && { articleCategory: d.articleCategory }),
        ...(typeof d.manualImei === "string" && { manualImei: d.manualImei }),
        ...(typeof d.purchasePrice === "number" && { purchasePrice: d.purchasePrice }),
        ...(typeof d.excluded === "boolean" && { excluded: d.excluded }),
        ...(d.updatedAt && { updatedAt: toDate(d.updatedAt) }),
    };
}

export function subscribeTransactionClassifications(
    callback: (overrides: TransactionClassificationOverride[]) => void,
    onError?: (error: Error) => void,
): Unsubscribe {
    return onSnapshot(
        transactionClassificationsCol,
        (snap) => {
            callback(snap.docs.map(transactionClassificationFromDoc));
        },
        onError,
    );
}

export async function saveTransactionClassification(
    id: string,
    data: Omit<TransactionClassificationOverride, "id" | "updatedAt">,
): Promise<void> {
    const payload: Record<string, unknown> = {
        updatedAt: Timestamp.now(),
        category: data.category || deleteField(),
        articleCategory: data.articleCategory || deleteField(),
        manualImei: data.manualImei || deleteField(),
        purchasePrice: (typeof data.purchasePrice === "number") ? data.purchasePrice : deleteField(),
        excluded: (typeof data.excluded === "boolean") ? data.excluded : deleteField(),
    };

    await setDoc(doc(db, "transaction_classifications", id), payload, { merge: true });
}

export async function deleteTransactionClassification(id: string): Promise<void> {
    await deleteDoc(doc(db, "transaction_classifications", id));
}
